"use client";

import React, { createContext, useState, useContext, useEffect } from "react";
import { BASE_URL } from "../config/apiConfig";
import { toast } from "react-toastify";

export const AssetContext = createContext();

export const AssetProvider = ({ children }) => {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Get token
  const getToken = () => localStorage.getItem("token");

  // Fetch all assets
  const fetchAssets = async () => {
    setLoading(true);
    setError("");

    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/assets`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      if (data.status === 200) {
        setAssets(data.data);
      } else {
        setError(data.message || "Failed to fetch assets");
        toast.error(data.message || "Failed to fetch assets");
      }
    } catch (err) {
      console.error(err);
      setError("Error fetching assets");
    } finally {
      setLoading(false);
    }
  };

  // Create asset assignment
  const createAsset = async (payload) => {
    setLoading(true);
    setError("");

    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/assets`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (data.status === 200) {
        await fetchAssets();
        toast.success(data.message || "Asset created successfully");
        return {
          success: true,
          message: "Asset created successfully",
        };
      }

      toast.error(data.message || "Failed to create asset");
      return {
        success: false,
        message: data.message,
      };
    } catch (err) {
      console.error(err);
      toast.error("Error creating asset");

      return {
        success: false,
        message: "Error creating asset",
      };
    } finally {
      setLoading(false);
    }
  };

  // Update asset
  const updateAsset = async (id, payload) => {
    setLoading(true);

    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/assets/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (data.status === 200) {
        await fetchAssets();
        toast.success(data.message || "Asset updated successfully");

        return {
          success: true,
          message: "Asset updated successfully",
        };
      }

      toast.error(data.message || "Failed to update asset");
      return {
        success: false,
        message: data.message,
      };
    } catch (err) {
      console.error(err);
      toast.error("Error updating asset");

      return {
        success: false,
        message: "Error updating asset",
      };
    } finally {
      setLoading(false);
    }
  };

  // Update asset status
  const updateAssetStatus = async (id, status, update_audit_id) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/assets/status/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          status,
          update_audit_id,
        }),
      });

      const data = await res.json();

      if (data.status === 200) {
        await fetchAssets();
        toast.success(data.message || "Asset status updated successfully");

        return {
          success: true,
          message: data.message,
        };
      }

      toast.error(data.message || "Failed to update asset status");
      return {
        success: false,
        message: data.message,
      };
    } catch (err) {
      console.error(err);
      toast.error("Error updating asset status");

      return {
        success: false,
        message: "Error updating asset status",
      };
    }
  };

  // Delete asset
  const deleteAsset = async (id, delete_audit_id) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/assets/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          delete_audit_id,
        }),
      });

      const data = await res.json();

      if (data.status === 200) {
        await fetchAssets();
        toast.success(data.message || "Asset deleted successfully");

        return {
          success: true,
          message: "Asset deleted successfully",
        };
      }

      toast.error(data.message || "Failed to delete asset");
      return {
        success: false,
        message: data.message,
      };
    } catch (err) {
      console.error(err);
      toast.error("Error deleting asset");

      return {
        success: false,
        message: "Error deleting asset",
      };
    }
  };

  useEffect(() => {
    const token = getToken();

    if (token) {
      fetchAssets();
    }
  }, []);

  return (
    <AssetContext.Provider
      value={{
        assets,
        loading,
        error,
        fetchAssets,
        createAsset,
        updateAsset,
        updateAssetStatus,
        deleteAsset,
      }}
    >
      {children}
    </AssetContext.Provider>
  );
};

export const useAssets = () => useContext(AssetContext);